
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

const LeadGenerationPage = () => {
  const features = [
    {
      icon: 'ri-target-line',
      title: 'Smart Targeting',
      description: 'AI analyzes market data to identify high-potential prospects in your area.'
    },
    {
      icon: 'ri-user-search-line',
      title: 'Behavioral Analysis',
      description: 'Track and analyze prospect behavior to optimize engagement strategies.'
    },
    {
      icon: 'ri-mail-send-line',
      title: 'Automated Outreach',
      description: 'Personalized email and SMS campaigns that convert prospects into clients.'
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Lead Scoring',
      description: 'AI-powered scoring system prioritizes leads based on conversion probability.'
    },
    {
      icon: 'ri-phone-line',
      title: 'Call Optimization',
      description: 'AI determines the best times to call prospects for maximum response rates.'
    },
    {
      icon: 'ri-dashboard-line',
      title: 'Real-time Analytics',
      description: 'Track campaign performance and ROI with detailed analytics dashboards.'
    }
  ];

  const plans = [
    {
      name: 'Starter',
      price: '$99',
      period: '/month',
      leads: '100',
      features: [
        'AI Lead Scoring',
        'Basic Email Automation',
        'CRM Integration',
        'Monthly Reports',
        'Email Support'
      ]
    },
    {
      name: 'Professional',
      price: '$199',
      period: '/month',
      leads: '500',
      features: [
        'Advanced AI Targeting',
        'Multi-channel Campaigns',
        'Behavioral Analytics',
        'Weekly Reports',
        'Phone Support',
        'Custom Templates'
      ],
      popular: true
    },
    {
      name: 'Enterprise',
      price: '$399',
      period: '/month',
      leads: 'Unlimited',
      features: [
        'Full AI Suite',
        'Predictive Analytics',
        'Custom Integrations',
        'Daily Reports',
        'Dedicated Manager',
        'White-label Options'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative py-20 bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(37, 99, 235, 0.8), rgba(37, 99, 235, 0.8)), url('https://public.readdy.ai/ai/img_res/6909ba5b35d91c7a3d77fb39410eb279.jpg')`
        }}
      >
        <div className="container mx-auto px-6 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl font-bold mb-6">
              AI-Powered Lead Generation
            </h1>
            <p className="text-xl mb-8 text-blue-100">
              Generate 10x more qualified leads with our intelligent targeting and automation system
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-12">
              <button className="bg-white text-blue-600 px-8 py-3 rounded-lg font-bold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer">
                Start Free Trial
              </button>
              <button className="border-2 border-white text-white px-8 py-3 rounded-lg font-bold hover:bg-white hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer">
                Watch Demo
              </button>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 text-center">
                <div className="text-3xl font-bold">300%</div>
                <div className="text-blue-100">More Leads</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 text-center">
                <div className="text-3xl font-bold">85%</div>
                <div className="text-blue-100">Higher Quality</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 text-center">
                <div className="text-3xl font-bold">60%</div>
                <div className="text-blue-100">Time Saved</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-gray-800">Intelligent Lead Generation Features</h2>
            <p className="text-xl text-gray-600">Advanced AI technology that works 24/7 to grow your business</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                  <i className={`${feature.icon} text-2xl text-blue-600`}></i>
                </div>
                <h3 className="text-xl font-bold mb-4 text-gray-800">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-gray-800">How It Works</h2>
            <p className="text-xl text-gray-600">Simple setup, powerful results</p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl font-bold">
                1
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-800">Connect Your Data</h3>
              <p className="text-gray-600">Integrate with your CRM and MLS for comprehensive data analysis</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl font-bold">
                2
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-800">AI Analysis</h3>
              <p className="text-gray-600">Our AI analyzes market trends and identifies high-potential prospects</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl font-bold">
                3
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-800">Automated Outreach</h3>
              <p className="text-gray-600">Personalized campaigns reach prospects at optimal times</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl font-bold">
                4
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-800">Convert & Close</h3>
              <p className="text-gray-600">Track engagement and convert qualified leads into clients</p>
            </div>
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-gray-800">Success Stories</h2>
            <p className="text-xl text-gray-600">Real results from real estate professionals</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="flex items-center mb-6">
                <img 
                  src="https://readdy.ai/api/search-image?query=professional%20female%20real%20estate%20agent%20headshot%2C%20confident%20business%20woman%2C%20modern%20office%20background&width=80&height=80&seq=agent1&orientation=squarish"
                  alt="Sarah Johnson"
                  className="w-16 h-16 rounded-full object-cover mr-4"
                />
                <div>
                  <h4 className="font-bold text-gray-800">Sarah Johnson</h4>
                  <p className="text-gray-600">Luxury Agent</p>
                </div>
              </div>
              <blockquote className="text-gray-700 mb-4">
                "Increased my lead generation by 400% in just 3 months. The AI targeting is incredibly accurate."
              </blockquote>
              <div className="text-blue-600 font-bold">+400% Lead Increase</div>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="flex items-center mb-6">
                <img 
                  src="https://readdy.ai/api/search-image?query=professional%20male%20real%20estate%20broker%20headshot%2C%20confident%20business%20man%2C%20modern%20office%20background&width=80&height=80&seq=agent2&orientation=squarish"
                  alt="Mike Chen"
                  className="w-16 h-16 rounded-full object-cover mr-4"
                />
                <div>
                  <h4 className="font-bold text-gray-800">Mike Chen</h4>
                  <p className="text-gray-600">Commercial Broker</p>
                </div>
              </div>
              <blockquote className="text-gray-700 mb-4">
                "The automated campaigns save me 20 hours per week while generating better quality leads."
              </blockquote>
              <div className="text-blue-600 font-bold">20 Hours Saved/Week</div>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="flex items-center mb-6">
                <img 
                  src="https://readdy.ai/api/search-image?query=professional%20female%20real%20estate%20agent%20headshot%2C%20confident%20business%20woman%2C%20modern%20office%20background&width=80&height=80&seq=agent3&orientation=squarish"
                  alt="Jennifer Lee"
                  className="w-16 h-16 rounded-full object-cover mr-4"
                />
                <div>
                  <h4 className="font-bold text-gray-800">Jennifer Lee</h4>
                  <p className="text-gray-600">Residential Agent</p>
                </div>
              </div>
              <blockquote className="text-gray-700 mb-4">
                "Closed 50% more deals this year thanks to the high-quality leads from the AI system."
              </blockquote>
              <div className="text-blue-600 font-bold">+50% More Closings</div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-gray-800">Choose Your Plan</h2>
            <p className="text-xl text-gray-600">Flexible pricing for every business size</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {plans.map((plan, index) => (
              <div key={index} className={`bg-white rounded-xl shadow-lg p-8 relative ${plan.popular ? 'ring-2 ring-blue-600' : ''}`}>
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-bold">
                      Most Popular
                    </span>
                  </div>
                )}
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold mb-4 text-gray-800">{plan.name}</h3>
                  <div className="text-4xl font-bold text-blue-600 mb-2">
                    {plan.price}<span className="text-lg text-gray-600">{plan.period}</span>
                  </div>
                  <p className="text-gray-600">Up to {plan.leads} leads</p>
                </div>
                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center">
                      <i className="ri-check-line text-green-500 mr-3"></i>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <button className={`w-full py-3 rounded-lg font-bold transition-colors whitespace-nowrap cursor-pointer ${
                  plan.popular 
                    ? 'bg-blue-600 text-white hover:bg-blue-700' 
                    : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                }`}>
                  Get Started
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to 10x Your Lead Generation?</h2>
          <p className="text-xl mb-8 text-blue-100">
            Join thousands of agents who have transformed their business with AI
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button className="bg-white text-blue-600 px-8 py-3 rounded-lg font-bold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer">
              Start Free Trial
            </button>
            <button className="border-2 border-white text-white px-8 py-3 rounded-lg font-bold hover:bg-white hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer">
              Schedule Demo
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LeadGenerationPage;
